import { useCallback, useEffect } from 'react';
import { apiFetch } from '../auth/apiFetch';
import { useCurrentUser } from '../auth/useCurrentUser';
import { usePlaidItems } from './usePlaidItems';
import { useTransactionsEnabled } from './useTransactionsEnabled';

// Module-level so multiple hook instances (Layout + DashboardPage) cooperate
// instead of each firing their own auto-sync. Reset on full page reload, which
// is the natural session boundary.
let autoFired = false;
let inFlight = false;

// Best-effort background Plaid sync. Mounted in Layout so it runs once per
// session regardless of landing page; DashboardPage also calls it to wire the
// Refresh button. Errors are swallowed so the rest of the page keeps loading.
export function usePlaidAutoSync(): { sync: () => void } {
  const me = useCurrentUser();
  const plaid = usePlaidItems();
  const { enabled } = useTransactionsEnabled();

  const eligible =
    enabled &&
    me.status === 'ready' &&
    me.mode === 'real' &&
    plaid.status === 'ready' &&
    plaid.items.length > 0;
  const reload = plaid.status === 'ready' ? plaid.reload : null;

  const sync = useCallback(() => {
    if (!eligible || inFlight) return;
    inFlight = true;
    void apiFetch('/api/plaid/sync', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    })
      .then((res) => {
        if (res.ok) reload?.();
      })
      .catch(() => {
        /* best-effort */
      })
      .finally(() => {
        inFlight = false;
      });
  }, [eligible, reload]);

  useEffect(() => {
    if (autoFired || !eligible) return;
    autoFired = true;
    sync();
  }, [eligible, sync]);

  return { sync };
}
